import { AppShell, MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { useDisclosure } from "@mantine/hooks";
import { Redirect, Route, Switch, useRoute, Router } from "wouter";
import { ErrorBoundary } from "@sentry/react";

import Confirmation from "../../Boilerplate/Auth/Confirmation";
import ForgotPassword from "../../Boilerplate/Auth/ForgotPassword";
import LoginForm from "../../Boilerplate/Auth/Login";
import LoginLinkHandler from "../../Boilerplate/Auth/LoginLink";
import ResetPassword from "../../Boilerplate/Auth/ResetPassword";
import ProfileEdit from "../../Boilerplate/Profile/ProfileEdit";
import UserList from "../../Boilerplate/Users/UserList";
import UserAdd from "../../Boilerplate/Users/UserAdd";
import UserEdit from "../../Boilerplate/Users/UserEdit";
import TenantList from "../../Boilerplate/AdminTenants/TenantList";
import TenantEdit from "../../Boilerplate/AdminTenants/TenantEdit";
import TenantCreate from "../../Boilerplate/AdminTenants/TenantCreate";
import TenantUserCreate from "../../Boilerplate/Tenants/TenantUserCreate";
import AdminUsersList from "../../Boilerplate/AdminUsers/AdminUsersList";
import AdminUserDetails from "../../Boilerplate/AdminUsers/AdminUserDetails";
import JobDetailsAdmin from "../../Boilerplate/AdminJobs/JobDetails";
import JobListAdmin from "../../Boilerplate/AdminJobs/Jobs";
import ChangePassword from "../../Boilerplate/Auth/ChangePassword";
import RegisterForm from "../../Boilerplate/Auth/Register";
import UserDetails from "../../Boilerplate/Users/UserDetails";
import InvitationForm from "../../Boilerplate/Auth/Invitation";

import ErrorFallback from "../../Boilerplate/_Shared/ErrorFallback";
import { HeaderMenu } from "./HeaderMenu";
import PaymentSuccess from "./Payment/Success";
import PaymentCancelled from "./Payment/Cancelled";
import PricingPage from "./Pricing/PricingPage";
import Navbar from "./NavBar";

import { store, useStore } from "./store";
import { NBoilerplateContext } from "../../Boilerplate/NBoilerplateContext";
import BrandList from "./Brands/BrandList";

import { shadcnCssVariableResolver } from "./cssVariableResolver";
import { shadcnTheme } from "./theme";
import "./theme.css";
import CriterionDetail from "./Brands/BrandAwareness/CriterionDetail";
import MonitorResultDetails from "./Brands/MonitorResultDetails";
import { useLlmProvidersStore } from "./LlmProviders/llmProvidersStore";
import { lazy, useEffect } from "react";
import AllAnalysisPrompts from "./Brands/AnalysisResults/AllAnalysisPrompts";
import { WebsiteList } from "./Websites";
import PrintView from "./Brands/PrintReport/PrintView";
import TenantProfileEdit from "./TenantProfile/TenantProfileEdit";
import Team from "./Team/Team";
import { ContentPlansList, ContentPlanDetail } from "./ContentPlans";
import ClusterReview from "./ContentPlans/ClusterReview";
import ClusterPage from "./ContentPlans/ClusterPage";
import SettingsPage from "./Settings/SettingsPage";
import LlmConfigPage from "./Settings/LlmConfigPage";
import AdminMonitoringDashboard from "./Admin/AdminMonitoringDashboard.jsx";
import { useBrandContext } from "./Brands/brandContext";
import BrandGuard from "./_Shared/BrandGuard";
import { useIsGlobalAdmin } from "./_Shared/useIsGlobalAdmin";
import { useCanUseContent } from "./_Shared/useCanUseContent";
import BrandAnalyticsTimeline from "./Brands/BrandAnalyticsTimeline";
import BrandMentionDetection from "./Brands/BrandMentionDetection";
import TrackingDashboard from "./Tracking/TrackingDashboard";
import SearchConsolePage from "./SearchConsole/SearchConsolePage";
import WebMentions from "./Analytics/WebMentions";
import ObjectivesPage from "./Objectives/ObjectivesPage";
import AllAnalysisResults from "./Brands/AnalysisResults/AllAnalysisResults";
import MediaGallery from "./Content/MediaGallery";

const ContentItemsList = lazy(() => import("./Content/ContentList.jsx"));
const ContentEditor = lazy(() => import("./Content/ContentEditor.jsx"));
const CategoryEditor = lazy(() => import("./Content/CategoryEditor.jsx"));
const DashboardPage = lazy(() => import("./Dashboard/DashboardPage.jsx"));
const OverviewPage = lazy(() => import("./Dashboard/OverviewPage.jsx"));

/** Redirect helper: sets brand context from URL param, then redirects */
function BrandRedirect({ params, to }) {
  const { selectBrandById } = useBrandContext();
  useEffect(() => {
    const brandId = params.id || params.brandId;
    if (brandId) selectBrandById(brandId);
  }, [params]);
  return <Redirect to={to} />;
}

/** Redirect old /analytics?tab=X to new flat routes */
function AnalyticsRedirect() {
  const searchString = new URLSearchParams(window.location.search);
  const tab = searchString.get("tab");
  const redirectMap = {
    "web-traffic": "/web-traffic",
    "ai-visibility": "/ai-visibility",
    "web-mentions": "/web-mentions",
    "reports": "/reports",
    "activity": "/dashboard",
    "overview": "/dashboard",
  };
  return <Redirect to={redirectMap[tab] || "/dashboard"} />;
}

function App() {
  const profile = useStore((state) => state.profile);
  const isGlobalAdmin = useIsGlobalAdmin();
  const canUseContent = useCanUseContent();
  const [opened, { toggle }] = useDisclosure(false);
  const { fetchLlmProviders, llmProviders } = useLlmProvidersStore();

  useEffect(() => {
    // Fetch LLM Providers if not already loaded
    if (!llmProviders || llmProviders.length === 0) {
      fetchLlmProviders();
    }
  }, [fetchLlmProviders]);

  // `match` is a boolean
  const [isPrint, printParams] = useRoute("/reports/:reportId/print");

  if (isPrint) {
    // If we're in print mode, we don't want to render the app shell, just the print view
    return (
      <MantineProvider
        theme={shadcnTheme}
        cssVariablesResolver={shadcnCssVariableResolver}
        defaultColorScheme="auto"
      >
        <PrintView params={printParams} />
      </MantineProvider>
    );
  }

  return (
    <Router>
      <NBoilerplateContext.Provider value={store}>
        <MantineProvider
          theme={shadcnTheme}
          cssVariablesResolver={shadcnCssVariableResolver}
          defaultColorScheme="auto"
        >
          <ModalsProvider>
            <Notifications />
            <AppShell
              header={{ height: 60 }}
              padding="xs"
              navbar={{
                width: 220,
                breakpoint: "sm",
                collapsed: { mobile: !opened, desktop: false },
              }}
            >
              <AppShell.Header>
                <ErrorBoundary fallback={ErrorFallback}>
                  <HeaderMenu opened={opened} toggle={toggle} />
                </ErrorBoundary>
              </AppShell.Header>
              <AppShell.Navbar p={0}>
                <ErrorBoundary fallback={ErrorFallback}>
                  <Navbar toggle={toggle} />
                </ErrorBoundary>
              </AppShell.Navbar>
              <AppShell.Main>
                <ErrorBoundary fallback={ErrorFallback}>
                  <Switch>
                    <Route path={`/websites`} component={WebsiteList} />
                    <Route path={`/home`} component={BrandList} />

                    {/* Overview + Dashboard */}
                    <Route path="/overview" component={OverviewPage} />
                    <Route path="/dashboard" component={DashboardPage} />

                    {/* Analytics — flat routes with BrandGuard */}
                    <Route path="/ai-visibility">
                      <BrandGuard requireActive>
                        {({ brand }) => <BrandAnalyticsTimeline brand={brand} />}
                      </BrandGuard>
                    </Route>
                    <Route path="/ai-detection">
                      <BrandGuard requireActive>
                        {({ brand }) => <BrandMentionDetection brand={brand} />}
                      </BrandGuard>
                    </Route>
                    <Route path="/web-traffic">
                      <BrandGuard requireActive>
                        <TrackingDashboard />
                      </BrandGuard>
                    </Route>
                    <Route path="/web-mentions">
                      <BrandGuard requireActive>
                        {({ brand }) => <WebMentions brand={brand} />}
                      </BrandGuard>
                    </Route>
                    {/* No requireActive: Search Console is linked during setup and its history
                        is the first real data a new brand has. Gating it behind activation would
                        hide the one thing that is already populated. */}
                    <Route path="/search-console">
                      <SearchConsolePage />
                    </Route>
                    <Route path="/reports">
                      <BrandGuard requireActive>
                        {({ brand }) => <AllAnalysisResults brandId={brand.id} />}
                      </BrandGuard>
                    </Route>

                    {/* Keyword Intelligence never filled: PrimaryKeyword is write-dead on prompts.
                        Real keyword data lives in Search Console. Keep the path so old links work. */}
                    <Route path="/keyword-intelligence">
                      <Redirect to="/search-console" />
                    </Route>

                    {/* Not gated on requireActive: an objective is chosen during setup, and a brand
                        with no objective is exactly the one that needs this page. */}
                    <Route path="/objectives" component={ObjectivesPage} />

                    {/* Settings & LLM Config */}
                    <Route path="/settings">
                      <BrandGuard>
                        <SettingsPage />
                      </BrandGuard>
                    </Route>
                    <Route path="/llm-config">
                      <BrandGuard requireActive>
                        {() => <LlmConfigPage />}
                      </BrandGuard>
                    </Route>

                    {/* Content, Media and Content Plans: paid plan. Keep child
                        order: /content/categories must stay ahead of /content/:id
                        or the id route swallows it. */}
                    {canUseContent && (
                      <>
                        <Route path="/content">
                          <BrandGuard>
                            <ContentItemsList />
                          </BrandGuard>
                        </Route>
                        <Route path="/content/categories">
                          <BrandGuard>
                            <CategoryEditor />
                          </BrandGuard>
                        </Route>
                        <Route path="/content/:id">
                          {(params) => (
                            <BrandGuard>
                              <ContentEditor params={params} />
                            </BrandGuard>
                          )}
                        </Route>

                        <Route path="/media">
                          <BrandGuard>
                            <MediaGallery />
                          </BrandGuard>
                        </Route>

                        <Route path="/plans">
                          <BrandGuard>
                            <ContentPlansList />
                          </BrandGuard>
                        </Route>
                        <Route path="/plans/:planId/clusters/:workstreamId/review">
                          <BrandGuard>
                            <ClusterReview />
                          </BrandGuard>
                        </Route>
                        <Route path="/plans/:planId/workstreams/:workstreamId/review">
                          <BrandGuard>
                            <ClusterReview />
                          </BrandGuard>
                        </Route>
                        <Route path="/plans/:planId/clusters/:workstreamId">
                          <BrandGuard>
                            <ClusterPage />
                          </BrandGuard>
                        </Route>
                        <Route path="/plans/:id">
                          <BrandGuard>
                            <ContentPlanDetail />
                          </BrandGuard>
                        </Route>
                      </>
                    )}

                    {/* Deep-link routes (keep — have sub-params) */}
                    <Route
                      path="/b/:brandId/monitors/:monitorId/results/:resultId"
                      component={MonitorResultDetails}
                    />
                    <Route
                      path="/b/:brandId/runs/:monitorRunId/prompts"
                      component={AllAnalysisPrompts}
                    />
                    <Route
                      path="/b/:brandId/awareness/:industry/:criterion"
                      component={CriterionDetail}
                    />

                    {/* Redirect old /analytics?tab=X routes */}
                    <Route path="/analytics" component={AnalyticsRedirect} />

                    {/* Redirect old /b/ routes to new top-level pages */}
                    <Route path="/b/:id/setup">
                      {(params) => <BrandRedirect params={params} to="/settings?tab=setup" />}
                    </Route>
                    <Route path="/b/:brandId/monitors">
                      {(params) => <BrandRedirect params={params} to="/llm-config?tab=monitors" />}
                    </Route>
                    <Route path="/b/:brandId/analytics">
                      {(params) => <BrandRedirect params={params} to="/ai-visibility" />}
                    </Route>
                    <Route path="/b/:brandId/prompts">
                      {(params) => <BrandRedirect params={params} to="/llm-config?tab=prompts" />}
                    </Route>
                    <Route path="/b/:brandId/runs">
                      {(params) => <BrandRedirect params={params} to="/reports" />}
                    </Route>
                    <Route path="/b/:id">
                      {(params) => <BrandRedirect params={params} to="/dashboard" />}
                    </Route>

                    {/* Boilerplate, User, Tenants etc. */}
                    <Route path={`/invite/:token`}>
                      {(params) => (
                        <InvitationForm appName="BrandKarma" params={params} />
                      )}
                    </Route>
                    <Route path={`/auth/reset`}>
                      <ForgotPassword realm="BKU" />
                    </Route>
                    <Route
                      path={`/auth/reset-password/:token`}
                      component={ResetPassword}
                    />
                    <Route path={`/auth/login`}>
                      <LoginForm realm="BKU" />
                    </Route>
                    <Route
                      path={`/auth/login/:token`}
                      component={LoginLinkHandler}
                    />
                    <Route
                      path={`/auth/confirm-email/:token`}
                      component={Confirmation}
                    />
                    <Route
                      path={`/auth/change-password`}
                      component={ChangePassword}
                    />
                    <Route path={`/auth/register`}>
                      <RegisterForm
                        realm="BKU"
                        touLink="https://www.getbrandkarma.com/legal"
                      />
                    </Route>
                    <Route path={`/profile`} component={ProfileEdit} />
                    <Route path={`/pricing`} component={PricingPage} />
                    <Route
                      path="/payment/completed"
                      component={PaymentSuccess}
                    />
                    <Route
                      path="/payment/cancelled"
                      component={PaymentCancelled}
                    />
                    {/* TENANT OWNER STUFF */}
                    {(profile?.role == "TenantOwner" ||
                      profile?.role == "TenantAdmin" ||
                      profile?.role == "GlobalAdmin") && (
                      <>
                        <Route path="/team" component={Team} />
                        <Route
                          path={`/settings/tenant`}
                          component={TenantProfileEdit}
                        />
                        <Route path={`/users`} component={UserList} />
                        <Route path={`/users/add`}>
                          <UserAdd realm="BKU" />
                        </Route>
                        <Route
                          path={`/users/details/:id`}
                          component={UserDetails}
                        />
                        <Route path={`/users/edit/:id`} component={UserEdit} />
                      </>
                    )}
                    {/* ADMIN STUFF */}
                    {profile?.role == "GlobalAdmin" && (
                      <>
                        <Route
                          path="/admin/monitoring"
                          component={AdminMonitoringDashboard}
                        />
                        <Route path="/admin/jobs" component={JobListAdmin} />
                        <Route
                          path="/admin/jobs/:id"
                          component={JobDetailsAdmin}
                        />
                        <Route path={`/tenants`} component={TenantList} />
                        <Route
                          path={`/tenants/create`}
                          component={TenantCreate}
                        />
                        <Route path={`/tenants/:id`} component={TenantEdit} />

                        <Route
                          path={`/tenants/:tenantId/users/create`}
                          component={TenantUserCreate}
                        />
                        <Route
                          path={`/admin/users`}
                          component={AdminUsersList}
                        />
                        <Route
                          path={`/admin/users/:userId`}
                          component={AdminUserDetails}
                        />
                      </>
                    )}
                    <Route
                      path="/"
                      component={() => {
                        if (profile?.role) {
                          return <Redirect to="/overview" />;
                        } else {
                          return <Redirect to="/auth/login" />;
                        }
                      }}
                    />
                    <Route>404, Not Found!</Route>
                  </Switch>
                </ErrorBoundary>
              </AppShell.Main>
            </AppShell>
          </ModalsProvider>
        </MantineProvider>
      </NBoilerplateContext.Provider>
    </Router>
  );
}

export default App;
